import React from 'react'
import { Tag } from 'antd'
import { FieldTypeEnum } from '../core/components/InputManager/types.ts'
import { EditingContext } from './table_grid'
import { FIELD_KEY_SEPARATOR } from '../state_manager/globals.ts'
import { DealFields } from '../state_manager/data_models/deal_record.ts'
import { AccountRecordFields } from '../state_manager/data_models/account_record.ts'
import { getTableCellValueForRecord } from '../core/utils/utilities.ts'
import type { ViewConfigColumn } from '../core/components/ViewConfig/types.ts'

import TaskListMini from '../app_modules/tasks/components/TaskListMini.tsx'
import TaskFilterDropdown from '../core/components/TaskFilterDropdown/TaskFilterDropdown.tsx'
import ContactFilterDropdown from '../core/components/ContactFilterDropdown/ContactFilterDropdown.tsx'
import type { ViewTaskConfig } from './fund_management/view_task_config.ts'

const forcedInputManagerFields: readonly string[] = [
  DealFields.prog__commitment_amount,
  AccountRecordFields.prog__investment_stats
]
const editableUserAddedFieldTypes = new Set([
  FieldTypeEnum.TEXT,
  FieldTypeEnum.TEXTAREA,
  FieldTypeEnum.URL,
  FieldTypeEnum.EMAIL,
  FieldTypeEnum.PHONE
])

function isAliasesColumn(valueKey: string): boolean {
  const normalized = String(valueKey || '').toLowerCase()
  return normalized === 'aliases' || normalized.endsWith('__aliases') || normalized.endsWith('_aliases')
}

interface NormalizedColumn {
  columnTitle: string
  valueKey: string
  dataKey?: string
  minWidth: number
  sortable: boolean
  filterable: boolean
  filterType?: string
  filterKey: string
  fieldType: string
  editable: boolean
  labelKey?: string
  freezeColumn: boolean
  moduleKey?: string
  fieldKey?: string
  tagDisplay: boolean
  isDefaultColumn: boolean
}

function toColumnConfig(c: ViewConfigColumn): NormalizedColumn {
  return {
    columnTitle: c.columnTitle,
    valueKey: c.valueKey,
    dataKey: c.dataKey,
    minWidth: c.minWidth ?? 150,
    sortable: c.sortable ?? false,
    filterable: c.filterable ?? false,
    filterType: c.filterType,
    filterKey: c.filterKey || c.valueKey,
    fieldType: c.fieldType ?? 'text',
    editable: c.editable ?? false,
    labelKey: c.labelKey,
    freezeColumn: c.freezeColumn ?? false,
    moduleKey: c.moduleKey,
    fieldKey: c.fieldKey,
    tagDisplay: c.tagDisplay ?? false,
    isDefaultColumn: c.isDefaultColumn ?? true
  }
}

function renderNonInputManagerCell(column: any, record: any, viewTaskConfig: any, handleTasksClickRef: any, handleTaskToggleRef: any, fallbackRecordModuleKey: string) {
  if (column.fieldType === FieldTypeEnum.TASKS) {
    const showTasks = (viewTaskConfig as ViewTaskConfig)?.showTasks !== false
    const taskConfig = viewTaskConfig as ViewTaskConfig
    if (!showTasks) {
      return null
    }
    const modForId = column.moduleKey || fallbackRecordModuleKey
    const recordWithId = {
      ...record,
      deals__id: record[modForId + FIELD_KEY_SEPARATOR + 'id'],
      accounts__id: record[modForId + FIELD_KEY_SEPARATOR + 'id']
    }
    const tasksKey = column.dataKey ?? column.valueKey
    return React.createElement(TaskListMini, {
      tasks: record[tasksKey],
      maxVisible: taskConfig?.maxVisible ?? 3,
      maxTextLength: 40,
      showCompleted: taskConfig?.showCompleted ?? false,
      onTaskClick: (task: any, allTasks: any[], currentRecord: any) => {
        if (handleTasksClickRef?.current) {
          handleTasksClickRef.current(allTasks, currentRecord, task.tasks__id)
        }
      },
      onTaskToggle: (taskId: string, recordId: string, completed: boolean) => {
        if (handleTaskToggleRef?.current) {
          handleTaskToggleRef.current(taskId, recordId, completed)
        }
      },
      record: recordWithId
    })
  }

  const recordKey = column.dataKey ?? column.valueKey
  const label = column.labelKey ? getTableCellValueForRecord(record, column.labelKey, column.moduleKey) : undefined
  const value = recordKey ? getTableCellValueForRecord(record, recordKey, column.moduleKey) : undefined
  const displayValue = label || value
  const shouldRenderAsTags =
    column.tagDisplay === true ||
    (column.fieldType === FieldTypeEnum.SELECT && column.valueKey === 'role') ||
    isAliasesColumn(column.valueKey)

  if (shouldRenderAsTags) {
    const toLabel = (entry: any): string | null => {
      if (entry == null) return null
      if (typeof entry === 'string') return entry.trim() || null
      if (typeof entry === 'number') return String(entry)
      if (typeof entry === 'object') {
        const maybeLabel = entry.label ?? entry.name ?? entry.value
        if (typeof maybeLabel === 'string') return maybeLabel.trim() || null
      }
      return null
    }

    const labels = Array.isArray(displayValue)
      ? displayValue.map(toLabel).filter((v): v is string => !!v)
      : typeof displayValue === 'string'
        ? displayValue
            .split(',')
            .map((part: string) => part.trim())
            .filter((part: string) => part.length > 0)
        : displayValue
          ? [String(displayValue)]
          : []

    if (labels.length > 0) {
      return React.createElement(
        'div',
        { className: 'flex flex-wrap gap-1' },
        labels.map((tagLabel: string, tagIndex: number) =>
          React.createElement(Tag, { key: `${column.valueKey}-${tagLabel}-${tagIndex}`, color: 'blue' }, tagLabel)
        )
      )
    }
  }

  const isNumericColumn = [
    FieldTypeEnum.NUMBER,
    FieldTypeEnum.CURRENCY,
    FieldTypeEnum.VALUATION,
    FieldTypeEnum.RATIO
  ].includes(column.fieldType)
  return isNumericColumn ? React.createElement('div', { className: 'text-right' }, displayValue) : displayValue
}

interface CreateTableColumnsArgs {
  columns: ViewConfigColumn[]
  /** Pin the first N *visible* columns (AntD fixed:'left'). Positional, so
   *  it tracks the actual leftmost column for aggregation-driven grids where
   *  that changes with the active grain / data-driven hiding / user reorder.
   *  Driven by cfg.reporting_config.freeze_leading_columns. */
  freezeLeadingColumns?: number
  fallbackRecordModuleKey?: string
  handlePrimaryActionRef?: { current?: ((...args: any[]) => any) | null }
  handleSecondaryActionRef?: { current?: ((...args: any[]) => any) | null }
  renderInputManagerColumnRef?: { current?: ((...args: any[]) => any) | null }
  handleTasksClickRef?: { current?: ((...args: any[]) => any) | null }
  handleTaskToggleRef?: { current?: ((...args: any[]) => any) | null }
  viewTaskConfig?: ViewTaskConfig
}

export const createTableColumns = ({
  handlePrimaryActionRef,
  handleSecondaryActionRef,
  renderInputManagerColumnRef,
  handleTasksClickRef,
  handleTaskToggleRef,
  viewTaskConfig,
  columns,
  freezeLeadingColumns = 0,
  fallbackRecordModuleKey = 'deals'
}: CreateTableColumnsArgs) => {
  const frozenColumns: string[] = []
  const transformedColumns: any[] = []
  const normalizedColumns = columns.map(toColumnConfig)

  for (const column of normalizedColumns) {
    const isUserAddedColumn = column.isDefaultColumn === false
    const effectiveSortable = isUserAddedColumn ? false : column.sortable
    const effectiveFilterable = isUserAddedColumn ? false : column.filterable
    let customFilterComponent: React.ComponentType<any> | undefined
    if (effectiveFilterable && column.fieldType === FieldTypeEnum.TASKS && column.filterType === 'custom') {
      const moduleKey = column.moduleKey
      customFilterComponent = (props: any) => {
        return React.createElement(TaskFilterDropdown, { ...props, moduleKey })
      }
    }

    if (effectiveFilterable && column.fieldType === FieldTypeEnum.CONTACT && column.filterType === 'custom') {
      const filterKey = column.filterKey || column.valueKey
      const filterLabel = column.columnTitle || ''
      customFilterComponent = (props: any) => {
        return React.createElement(ContactFilterDropdown, { ...props, filterKey, filterLabel })
      }
    }

    const dataIndex = column.dataKey ?? column.valueKey
    transformedColumns.push({
      title: column.columnTitle,
      dataIndex,
      key: column.valueKey,
      width: column.minWidth,
      sorter: effectiveSortable,
      filterDropdown: effectiveFilterable,
      filterType: column.filterType,
      filterKey: column.filterKey || column.valueKey,
      // Surface fieldType on the AntD column so the filter UI (notably
      // RangeFilterDropdown) can drive numeric formatting and percent
      // conversion from cfg, not from a hardcoded list.
      fieldType: column.fieldType,
      customFilterComponent,
      render: (_: any, record: any, index: number, editingContext: EditingContext) => {
        const shouldForceReadonlyInputManager =
          isUserAddedColumn &&
          column.fieldType !== FieldTypeEnum.TASKS &&
          !editableUserAddedFieldTypes.has(column.fieldType as FieldTypeEnum)
        const effectiveEditable = shouldForceReadonlyInputManager ? false : column.editable
        const inputFieldKey = column.dataKey ?? column.valueKey
        const shouldUseInputManager =
          forcedInputManagerFields.includes(inputFieldKey) || effectiveEditable || shouldForceReadonlyInputManager

        if (!shouldUseInputManager) {
          return renderNonInputManagerCell(
            column,
            record,
            viewTaskConfig,
            handleTasksClickRef,
            handleTaskToggleRef,
            fallbackRecordModuleKey
          )
        }

        const handlePrimaryAction = handlePrimaryActionRef?.current
        const handleSecondaryAction = handleSecondaryActionRef?.current
        const renderInputManagerColumn = renderInputManagerColumnRef?.current

        if (!renderInputManagerColumn) {
          return renderNonInputManagerCell(
            column,
            record,
            viewTaskConfig,
            handleTasksClickRef,
            handleTaskToggleRef,
            fallbackRecordModuleKey
          )
        }

        const recordModuleKey = column.moduleKey || fallbackRecordModuleKey
        const effectiveTagDisplay = column.tagDisplay === true || isAliasesColumn(inputFieldKey)
        const effectiveFieldType = isAliasesColumn(inputFieldKey)
          ? FieldTypeEnum.TAGS
          : (column.fieldType as FieldTypeEnum)
        return renderInputManagerColumn(
          record,
          inputFieldKey,
          recordModuleKey + FIELD_KEY_SEPARATOR + 'id',
          effectiveFieldType,
          editingContext,
          {
            title: column.columnTitle,
            editable: effectiveEditable,
            tagDisplay: effectiveTagDisplay,
            moduleKey: recordModuleKey,
            isDefaultColumn: column.isDefaultColumn
          },
          column.fieldType === FieldTypeEnum.LINK
            ? column.fieldKey === 'open-record-drawer'
              ? handlePrimaryAction
              : handleSecondaryAction
            : undefined
        )
      }
    })

    if (column.freezeColumn === true) {
      frozenColumns.push(column.valueKey)
    }
  }

  // Positional pin: the first N visible columns (in render order) are the
  // contiguous left block AntD requires for fixed:'left'. Tracks the actual
  // leftmost column for aggregation-driven grids.
  if (freezeLeadingColumns > 0) {
    for (const c of normalizedColumns.slice(0, freezeLeadingColumns)) {
      if (!frozenColumns.includes(c.valueKey)) frozenColumns.push(c.valueKey)
    }
  }

  return { columns: transformedColumns, frozenColumns }
}
